import React, { memo, useCallback, useMemo, useSyncExternalStore } from 'react';
import { MAX_ZOOM_LEVEL, ZOOM_STEP, zoomLevelStore } from '../store/zoomLevel';
import IconButton from '@/components/atoms/Button/IconButton';
import { FPS_24 } from '@/App/remotion/constants';
import useRemotionPlayerDuration from '@/App/remotion/MainPlayer/ClipPlayerV2WithNewTimeline/hooks/useRemotionPlayerDuration';

function ZoomRange({ timelineContainerRef }: { timelineContainerRef: React.RefObject<HTMLDivElement> }) {
  const fullRecordingDuration = useRemotionPlayerDuration();
  const zoomValue = useSyncExternalStore(zoomLevelStore.subscribe, zoomLevelStore.getSnapshot);
  const setZoomValue = useCallback((value: number) => {
    zoomLevelStore.set(() => value);
  }, []);
  const onZoomChange = useCallback(e => setZoomValue(+e.target.value), [setZoomValue]);
  const minZoom = useMemo(
    () => (timelineContainerRef.current?.clientWidth || Infinity) / (fullRecordingDuration * FPS_24) / 2,
    [fullRecordingDuration, timelineContainerRef]
  );

  const onZoomIncrease = useCallback(() => {
    // !! Make the increment to 10% of complete range
    setZoomValue(Math.min(MAX_ZOOM_LEVEL, zoomValue * 1.2));
  }, [setZoomValue, zoomValue]);

  const onZoomDecrease = useCallback(() => {
    setZoomValue(Math.max(minZoom, zoomValue * 0.8));
  }, [setZoomValue, zoomValue, minZoom]);

  return (
    <div className="flex items-center justify-center gap-2">
      <IconButton
        icon="IconZoomOut"
        onClick={onZoomDecrease}
        iconClassName="cursor-pointer"
        buttonClassName="w-9 h-9 border-none"
        variation="checkbox"
        trackingId="timeline-zoom-out"
      />
      <input
        type="range"
        min={minZoom}
        max={MAX_ZOOM_LEVEL}
        value={zoomValue}
        step={ZOOM_STEP}
        onChange={onZoomChange}
        className="h-1 w-24 cursor-pointer appearance-none rounded-xl bg-slate-300"
      />
      <IconButton
        icon="IconZoomIn"
        onClick={onZoomIncrease}
        iconClassName="cursor-pointer"
        buttonClassName="w-9 h-9 border-none"
        variation="checkbox"
        trackingId="timeline-zoom-in"
      />
    </div>
  );
}

export default memo(ZoomRange);
